"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.accountSchema = exports.exportRequestSchema = exports.EXPORT_STATUSES = exports.createdTokenSchema = exports.createTokenRequestSchema = exports.tokenSchema = exports.PERSONAL_ACCESS_TOKEN_PREFIX = exports.updateNotificationsRequestSchema = exports.notificationSettingsSchema = exports.notificationChannelSchema = exports.EMAIL_ONLY_EVENTS = exports.NOTIFICATION_EVENT_GROUPS = exports.NOTIFICATION_EVENTS = exports.updatePreferencesRequestSchema = exports.preferencesSchema = exports.WEEK_STARTS = exports.DATE_FORMATS = exports.LOCALES = exports.updateProfileRequestSchema = exports.profileSchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const token_scopes_1 = require("../permissions/token-scopes");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The account-settings wire shapes. Defined once (CLAUDE.md §3).            │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
/* ── profile ─────────────────────────────────────────────────────────────────────────────── */
exports.profileSchema = zod_1.z.object({
    email: zod_1.z.string(),
    displayName: zod_1.z.string(),
    /** ⚠️ Item 6 · the two halves. Both nullable — `personSchema` carries the argument. */
    givenName: zod_1.z.string().nullable(),
    familyName: zod_1.z.string().nullable(),
    phone: zod_1.z.string().nullable(),
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **`email` is NOT here, and that is the decision.** Changing an address is a GoTrue flow with
 * a confirmation mail and a pending state — it cannot be one field in a form that also renames
 * you, because a partial success would leave the screen unable to say which half happened.
 * `POST /account/email` owns it.
 *
 * ⚠️ **`.nullable()` on the optional fields, not `.optional()`.** The screen clears a phone number
 * by emptying the input, and `undefined` cannot express "clear this" — the same distinction the
 * role update needed for its description. An absent key means "leave it"; `null` means "remove it".
 */
exports.updateProfileRequestSchema = zod_1.z.object({
    /**
     * ⚠️⚠️ **OPTIONAL, BECAUSE THE FORM NO LONGER ASKS FOR IT.** Item 6 split the name in two and left
     * a *Name* box above the halves; the product owner asked on 10 Sep 2026 why people still have a
     * separate full-name field, having asked once already. The two halves are the name now, and
     * `0101`'s trigger composes `display_name` from them — so a form that sent all three was
     * offering to set a value the database was about to overwrite.
     *
     * ⚠️ **Still ACCEPTED**, and that is not hedging: an older client sends only this, and an
     * invitation creates a member with nothing else. Absent means *leave it alone*, which is what
     * lets the halves be the only thing on the wire.
     */
    displayName: zod_1.z.string().min(1).max(200).optional(),
    givenName: zod_1.z.string().trim().min(1).max(100).optional(),
    familyName: zod_1.z.string().trim().min(1).max(100).optional(),
    phone: zod_1.z.string().max(40).nullable().optional(),
    avatarUrl: zod_1.z.string().max(2048).nullable().optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── preferences ─────────────────────────────────────────────────────────────────────────── */
exports.LOCALES = ['hu', 'en'];
exports.DATE_FORMATS = ['YYYY.MM.DD.', 'DD/MM/YYYY', 'MM/DD/YYYY'];
exports.WEEK_STARTS = ['mon', 'sun'];
exports.preferencesSchema = zod_1.z.object({
    locale: zod_1.z.enum(exports.LOCALES),
    timezone: zod_1.z.string(),
    dateFormat: zod_1.z.enum(exports.DATE_FORMATS),
    weekStart: zod_1.z.enum(exports.WEEK_STARTS),
    theme: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
exports.updatePreferencesRequestSchema = zod_1.z.object({
    locale: zod_1.z.enum(exports.LOCALES),
    /**
     * ⚠️ **Not validated against a list here.** The database checks it by asking Postgres to *use*
     * the zone (`is_valid_timezone`), which is its own tz database answering — a list in this file
     * goes stale the next time a government moves a boundary, and the failure would be refusing a
     * zone that works.
     */
    timezone: zod_1.z.string().min(1).max(64),
    dateFormat: zod_1.z.enum(exports.DATE_FORMATS),
    weekStart: zod_1.z.enum(exports.WEEK_STARTS),
    /* Free text for the same reason the column is: the eight themes live in `packages/ui`, plus
     * `'system'`, and a ninth is a design decision rather than a schema change. */
    theme: zod_1.z.string().min(1).max(40),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── notifications ───────────────────────────────────────────────────────────────────────── */
exports.NOTIFICATION_EVENTS = [
    'assigned',
    'status',
    'comment',
    'due',
    /* `0182` · phase 3 of the notification emails. Requests waiting for ME; what happens to MINE;
       a copy of what I just did myself. */
    'approval',
    'request',
    'receipt',
    /* `0185` · phase 4. A contract's watch; a project's changes; the daily project alerts digest. */
    'contract',
    'project',
    'alerts',
    /* `0188` · phase 5. My missing hours (the week, the lock); my team's; finance; integrations. */
    'logging',
    'team',
    'finance',
    'integrations',
];
/**
 * How the settings matrix groups its rows — the mockup's email groups, as far as phase 3 reaches.
 *
 * ⚠️ **A shared constant, not the screen's choice**, so the API that seeds defaults and the screen
 * that draws them agree on which events exist and in what order.
 */
exports.NOTIFICATION_EVENT_GROUPS = [
    { id: 'tasks', events: ['assigned', 'status', 'comment', 'due'] },
    { id: 'requests', events: ['approval', 'request', 'receipt'] },
    { id: 'projects', events: ['project', 'alerts', 'contract'] },
    { id: 'time', events: ['logging', 'team'] },
    { id: 'workspace', events: ['finance', 'integrations'] },
];
/**
 * ⚠️ **Events with no bell row** — a receipt is a copy of the person's own act, and a bell entry
 * for your own click is noise. The matrix draws no in-app box for these.
 */
exports.EMAIL_ONLY_EVENTS = [
    'receipt',
    'alerts',
    /* Phase 5 · digests and warnings about a period, not events with a row to open. */
    'logging',
    'team',
    'finance',
    'integrations',
];
exports.notificationChannelSchema = zod_1.z.object({
    event: zod_1.z.enum(exports.NOTIFICATION_EVENTS),
    email: zod_1.z.boolean(),
    inApp: zod_1.z.boolean(),
});
exports.notificationSettingsSchema = zod_1.z.object({
    paused: zod_1.z.boolean(),
    dueLeadDays: zod_1.z.number().int(),
    reminderEnabled: zod_1.z.boolean(),
    /** `HH:MM`. A wall-clock time in the user's own zone — see the column's note. */
    reminderAt: zod_1.z.string(),
    /** ISO weekdays, 1 = Monday. */
    reminderDays: zod_1.z.array(zod_1.z.number().int().min(1).max(7)),
    weeklyDigest: zod_1.z.boolean(),
    channels: zod_1.z.array(exports.notificationChannelSchema),
    version: zod_1.z.number().int(),
});
exports.updateNotificationsRequestSchema = zod_1.z.object({
    paused: zod_1.z.boolean(),
    dueLeadDays: zod_1.z.number().int().min(1).max(14),
    reminderEnabled: zod_1.z.boolean(),
    /* ⚠️ `HH:MM` only — no seconds, no zone. The column is a `time`, the picker offers minutes, and
     * accepting `16:30:00.000Z` would invite somebody to send an instant for a wall-clock field. */
    reminderAt: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM in 24-hour form'),
    /* ⚠️ Non-empty, because an empty set is "a reminder that never fires" — which is what
     * `reminderEnabled: false` is for, and two ways to say one thing is how they come to disagree. */
    reminderDays: zod_1.z.array(zod_1.z.number().int().min(1).max(7)).min(1).max(7),
    weeklyDigest: zod_1.z.boolean(),
    /* The complete matrix, like the role grant map: every event the sender is aware of, so an
     * omission is a value rather than a gap. */
    channels: zod_1.z.array(exports.notificationChannelSchema).max(exports.NOTIFICATION_EVENTS.length),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── personal access tokens ──────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C51.1 · THE PREFIX IS A CONSTANT BECAUSE TWO SIDES BRANCH ON IT.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `AccountService.createToken` puts it on; `RequestContext.resolve` decides from it whether a
 * bearer is a token or a GoTrue JWT, **before** either is verified. Two hand-typed copies of a
 * string with that job is one typo away from every token being handed to the JWKS verifier, whose
 * refusal would say the token was rejected by a verifier it was never for.
 *
 * ⚠️ It is also what makes a leaked credential greppable: a string beginning `fcf_pat_` in a log,
 * a config file or a pasted snippet is unambiguously a live key, which is the property secret
 * scanners are built around.
 */
exports.PERSONAL_ACCESS_TOKEN_PREFIX = 'fcf_pat_';
exports.tokenSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `token_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('token'),
    name: zod_1.z.string(),
    /** The last four characters, so two tokens are distinguishable without storing a usable one. */
    lastFour: zod_1.z.string(),
    createdAt: zod_1.z.string(),
    lastUsedAt: zod_1.z.string().nullable(),
    revokedAt: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C51.1 · which workspace this token may act in, and what it may do there.** Both are on
     * the summary because both are things somebody revoking a token needs to see without opening it
     * — `0120` makes them required on any live row, and a list that showed neither would be a list
     * of identical-looking keys with different authority.
     */
    workspaceId: zod_1.z.string().nullable(),
    scopes: zod_1.z.array(token_scopes_1.tokenScopeSchema),
});
exports.createTokenRequestSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(120),
    /**
     * ⚠️ **Required, both of them, and neither has a default.** A token minted without a workspace
     * or without a scope set would be the blank cheque R-39 exists to prevent — and defaulting the
     * scopes to "everything the holder can do" would make the safe choice the one somebody has to
     * remember to make. The mint dialog picks; the server refuses to guess.
     */
    workspaceId: zod_1.z.uuid(),
    scopes: zod_1.z.array(token_scopes_1.tokenScopeSchema).min(1),
});
/**
 * ⚠️ **The ONLY response that ever carries the token value, and it is not stored anywhere.** The
 * database holds a hash and the last four characters; this shape exists for exactly one response,
 * to exactly one request, and the screen shows it in a reveal-once dialog. A `GET` that returned
 * it would make the hash pointless.
 */
exports.createdTokenSchema = exports.tokenSchema.extend({
    value: zod_1.z.string(),
});
/* ── export ──────────────────────────────────────────────────────────────────────────────── */
exports.EXPORT_STATUSES = ['requested', 'running', 'ready', 'failed', 'expired'];
exports.exportRequestSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `export_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('export'),
    status: zod_1.z.enum(exports.EXPORT_STATUSES),
    createdAt: zod_1.z.string(),
    completedAt: zod_1.z.string().nullable(),
    /** Present only while `ready` and unexpired. An export contains everything you can see. */
    downloadUrl: zod_1.z.string().nullable(),
    expiresAt: zod_1.z.string().nullable(),
});
/* ── the whole screen in one response ────────────────────────────────────────────────────── */
/**
 * ⚠️ **ONE request for the whole screen, not seven.** The account settings page has seven sections
 * behind a sidebar and somebody clicking between them is not making seven round trips — nor should
 * the first paint wait on the slowest of seven. Sessions come from `/auth/sessions`, which already
 * exists and is the one genuinely separate concern (it is about *this* browser).
 */
exports.accountSchema = zod_1.z.object({
    profile: exports.profileSchema,
    preferences: exports.preferencesSchema,
    notifications: exports.notificationSettingsSchema,
    tokens: zod_1.z.array(exports.tokenSchema),
    /** The most recent request, or null. The screen needs only the live one. */
    latestExport: exports.exportRequestSchema.nullable(),
});
//# sourceMappingURL=contract.js.map